import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import { AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { createEsicMonthlyContribution, deleteEsicMonthlyContribution, fetchEsicMonthlyContributionList, updateEsicMonthlyContribution } from "../../../../core/data/redux/payroll/ESIC/Master/MonthlyContribution/esicMonthlyContributionAction";
import DeleteModal1 from "../../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../../core/common/GlobalFunctions/exportTableData";
import { Button, Drawer, message, Select } from "antd";
import { decryptData } from "../../../../core/utils/encryptUtils";
import { useAppConfig } from "../../../../customhooks/useAppConfig";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { fetchEsicEmployees } from "../../../../core/data/redux/payroll/ESIC/employeeMapping/employeemappingAction";
import { fetchEmployeeSalaryStructures } from "../../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { fetchEarnings } from "../../../../core/data/redux/payroll/payrollComponent/earning/earningActions";
import { fetchEsicConfigurationList } from "../../../../core/data/redux/payroll/ESIC/Master/Configuration/configurationActions";
import { formatCurrency } from "../../../../core/utils/configFormatters";

dayjs.extend(utc);

/* ---------- Helpers (stable refs) ---------- */
const isISO = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z$/.test(s);
const isDDMMYYYY = (s?: string) => !!s && /^\d{2}-\d{2}-\d{4}$/.test(s);
const isMMYYYY = (s?: string) => !!s && /^\d{2}-\d{4}$/.test(s);

const toMonthPayload = (monthInputYYYYMM: string) => {
  if (!monthInputYYYYMM) return "";
  const [yyyy, mm] = monthInputYYYYMM.split("-");
  return `${mm}-${yyyy}`; // MM-YYYY
};

const toDMY = (dateYYYYMMDD: string) => {
  if (!dateYYYYMMDD) return "";
  const d = dayjs(dateYYYYMMDD, "YYYY-MM-DD");
  return d.isValid() ? d.format("DD-MM-YYYY") : "";
};

const salaryMonthToInput = (val?: string) => {
  if (!val) return "";
  if (isISO(val)) return dayjs.utc(val).local().format("YYYY-MM");
  if (isMMYYYY(val)) {
    const [mm, yyyy] = val.split("-");
    return `${yyyy}-${mm}`;
  }
  const d = dayjs(val);
  return d.isValid() ? d.local().format("YYYY-MM") : "";
};

const paymentDateToInput = (val?: string | null) => {
  if (!val) return "";
  if (isISO(val)) return dayjs.utc(val).local().format("YYYY-MM-DD");
  if (isDDMMYYYY(val)) {
    const d = dayjs(val, "DD-MM-YYYY");
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
  }
  const d = dayjs(val);
  return d.isValid() ? d.local().format("YYYY-MM-DD") : "";
};

const formatINR = (n: number | string) =>
  Number(n ?? 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const parseAnyDate = (val?: string | null): Dayjs | null => {
  if (!val) return null;
  if (isISO(val)) return dayjs.utc(val);
  if (isDDMMYYYY(val)) return dayjs(val, "DD-MM-YYYY");
  if (isMMYYYY(val)) {
    const [mm, yyyy] = val.split("-");
    return dayjs(`${yyyy}-${mm}-01`, "YYYY-MM-DD");
  }
  const d = dayjs(val);
  return d.isValid() ? d : null;
};

const overlapsMonth = (startISO?: string, endISO?: string, ym?: string) => {
  if (!startISO || !ym) return false;
  const start = dayjs(startISO);
  const end = endISO ? dayjs(endISO) : dayjs("2999-12-31");
  const ymStart = dayjs(`${ym}-01`);
  const ymEnd = ymStart.endOf("month");
  return !(end.isBefore(ymStart, "day") || start.isAfter(ymEnd, "day"));
};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const todayDMY = () => dayjs().format("DD-MM-YYYY");

const sortableEpoch = (val?: string | null) => {
  if (!val) return 0;
  const mFull = val.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO or YYYY-MM-DD
  if (mFull) return new Date(`${mFull[1]}-${mFull[2]}-${mFull[3]}T00:00:00`).getTime();
  const mMonth = val.match(/^(\d{2})-(\d{4})$/); // MM-YYYY
  if (mMonth) return new Date(`${mMonth[2]}-${mMonth[1]}-01T00:00:00`).getTime();
  return 0;
};

const formatSalaryMonth = (val?: string | null) => {
  if (!val) return "N/A";
  if (isISO(val)) return dayjs.utc(val).local().format("MMM YYYY");
  const full = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (full) return dayjs(`${full[1]}-${full[2]}-${full[3]}`).format("MMM YYYY");
  const mon = val.match(/^(\d{2})-(\d{4})$/);
  if (mon) return dayjs(`${mon[2]}-${mon[1]}-01`).format("MMM YYYY");
  return "N/A";
};

const formatPaymentDate = (val?: string | null) => {
  if (!val) return "—";
  if (isISO(val)) return dayjs.utc(val).local().format("DD-MM-YYYY");
  const dmy = val.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return val;
  const ymd = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) return dayjs(`${ymd[1]}-${ymd[2]}-${ymd[3]}`).format("DD-MM-YYYY");
  return "—";
};

const monthKey = (val?: string | null) => {
  if (!val) return "";
  if (isISO(val)) return dayjs.utc(val).local().format("YYYY-MM");
  if (isMMYYYY(val)) {
    const [mm, yyyy] = String(val).split("-");
    return `${yyyy}-${mm}`;
  }
  const d = dayjs(val);
  return d.isValid() ? d.local().format("YYYY-MM") : "";
};

/* ------------------------------------------- */

const MonthlyContribution = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const [tableKey, setTableKey] = useState(0);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const { earnings } = useSelector((state: any) => state.payroll.earning);
  const { salaryStructures = [] } = useSelector((state: any) => state.payroll.salaryStructure);
  const { list = [], loading } = useSelector((state: any) => state.esic.monthlycontribution);
  const { records: esicEmployees = [] } = useSelector((state: any) => state.esic.esicEmployee);
  const { configList = [] } = useSelector((state: any) => state.esic.configuration);

  // form state
  const [esicEmpId, setEsicEmpId] = useState<string>("");
  const [salaryMonth, setSalaryMonth] = useState<string>("");
  const [grossWages, setGrossWages] = useState<string>("");
  const [esicWages, setEsicWages] = useState<string>("");
  const [employeeContribution, setEmployeeContribution] = useState<string>("");
  const [employerContribution, setEmployerContribution] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("Pending");
  const [paymentDate, setPaymentDate] = useState<string>("");
  const [challanNumber, setChallanNumber] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [selectedEarningIds, setSelectedEarningIds] = useState<number[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [grossCap, setGrossCap] = useState<number>(0);

  // bulk drawer
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMonth, setBulkMonth] = useState<string>("");
  const [bulkStatus, setBulkStatus] = useState<"Pending" | "Paid">("Pending");
  const [bulkEmpPct, setBulkEmpPct] = useState<number>(0);
  const [bulkErPct, setBulkErPct] = useState<number>(0);
  const [bulkHeadIds, setBulkHeadIds] = useState<number[]>([]);
  const [bulkConfigId, setBulkConfigId] = useState<number | null>(null);

  // percent inputs (single entry drawer)
  const [empPct, setEmpPct] = useState<number>(0);
  const [erPct, setErPct] = useState<number>(0);

  const [filterMonth, setFilterMonth] = useState<string>(dayjs().format("YYYY-MM"));

  const calcAmt = (gross: number, pct: number) => round2((gross * (pct || 0)) / 100);

  const [errors, setErrors] = useState<any>({});

  const contributionId = selectedRowData?.contribution_id;

  // permissions
  const encryptedPermissions = localStorage.getItem("currentPermissions");
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || "")) ? decryptData(encryptedPermissions || "") : [];
  const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");
  const canView = permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit = permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete = permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport = permissions.includes("Export") || permissions.includes("AllowAll");

  useEffect(() => {
    dispatch(fetchEsicConfigurationList());
  }, [dispatch]);

  // derive contributions from gross + %
  useEffect(() => {
    const g = Number(grossWages || 0);
    setEmployeeContribution(String(calcAmt(g, Number(empPct))));
    setEmployerContribution(String(calcAmt(g, Number(erPct))));
  }, [grossWages, empPct, erPct]);

  // initial data fetch (consolidated)
  useEffect(() => {
    if (!list.length) dispatch(fetchEsicMonthlyContributionList());
    dispatch(fetchEmployeeSalaryStructures());
    dispatch(fetchEarnings());
    dispatch(fetchEsicEmployees());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // edit prefill (types fixed + pct prefill)
  useEffect(() => {
    if (modalType === "edit" && selectedRowData) {
      setEsicEmpId(String(selectedRowData?.esic_emp_id ?? ""));
      const cfgId = selectedRowData?.selectedConfigId != null ? Number(selectedRowData.selectedConfigId) : null;
      setSelectedConfigId(cfgId);

      setSalaryMonth(salaryMonthToInput(selectedRowData?.salary_month));
      setGrossWages(String(selectedRowData?.gross_wages ?? ""));
      setEsicWages(String(selectedRowData?.esic_wages ?? ""));
      setEmployeeContribution(String(selectedRowData?.employee_contribution ?? ""));
      setEmployerContribution(String(selectedRowData?.employer_contribution ?? ""));
      setPaymentStatus(selectedRowData?.payment_status ?? "Pending");
      setPaymentDate(paymentDateToInput(selectedRowData?.payment_date ?? null));
      setChallanNumber(selectedRowData?.challan_number ?? "");
      setSelectedEarningIds(
        Array.isArray(selectedRowData?.Selected_Earning_IDs)
          ? selectedRowData.Selected_Earning_IDs.map((id: any) => Number(id))
          : []
      );

      // Percentages: prefer config; else back-calc from amounts
      const cfg = cfgId ? configList.find((c: any) => Number(c.config_id) === Number(cfgId)) : null;
      if (cfg) {
        setEmpPct(Number(cfg.employee_contribution_rate) || 0);
        setErPct(Number(cfg.employer_contribution_rate) || 0);
        setGrossCap(Number(cfg.wage_limit) > 0 ? Number(cfg.wage_limit) : 0);
      } else {
        const gross = Number(selectedRowData?.gross_wages || 0);
        const esic = Number(selectedRowData?.esic_wages || 0);
        const base = esic > 0 ? esic : gross;
        const empAmt = Number(selectedRowData?.employee_contribution || 0);
        const erAmt = Number(selectedRowData?.employer_contribution || 0);
        const emp = base > 0 ? round2((empAmt / base) * 100) : 0;
        const er = base > 0 ? round2((erAmt / base) * 100) : 0;
        setEmpPct(emp);
        setErPct(er);
        setGrossCap(0);
      }
    }
  }, [modalType, selectedRowData, configList]);

  // set employeeData from selected esicEmpId
  useEffect(() => {
    if (esicEmpId) {
      const esicEmployee = esicEmployees.find((rec: any) => rec.esic_emp_id === parseInt(esicEmpId, 10));
      if (esicEmployee) {
        const salaryData = salaryStructures.find((emp: any) => emp.employee_id === esicEmployee.employee_id);
        setEmployeeData(salaryData || null);
      }
    } else {
      setEmployeeData(null);
    }
  }, [esicEmpId, esicEmployees, salaryStructures]);

  // default earnings selection & gross when creating (NOT during edit)
  useEffect(() => {
    if (modalType === "create") {
      if (employeeData?.earnings?.length) {
        const ids = employeeData.earnings.map((e: any) => Number(e.id));
        setSelectedEarningIds(ids);
        const gross = employeeData.earnings.reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0);
        setGrossWages(String(gross));
      } else {
        setSelectedEarningIds([]);
        setGrossWages("");
      }
    }
  }, [employeeData, modalType]);

  const resetFields = () => {
    setEsicEmpId("");
    setSalaryMonth("");
    setGrossWages("");
    setEsicWages("");
    setEmployeeContribution("");
    setEmployerContribution("");
    setPaymentStatus("Pending");
    setPaymentDate("");
    setChallanNumber("");
    setSelectedRowData(null);
    setSelectedEarningIds([]);
    setEmployeeData(null);
    setSelectedConfigId(null);
    setEmpPct(0);
    setErPct(0);
    setGrossCap(0);
  };

  const resetBulkFields = () => {
    setBulkMonth("");
    setBulkStatus("Pending");
    setBulkEmpPct(0);
    setBulkErPct(0);
    setBulkHeadIds([]);
    setBulkConfigId(null);
    setGrossCap(0);
  };

  const handleBulkClose = () => {
    setBulkOpen(false);
    resetBulkFields();
  };

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    resetFields();
    setErrors({});
  };

  const handleEdit = (record: any) => setSelectedRowData(record);

  // validation
  const validateForm = () => {
    const newErrors: any = {};
    const numOrErr = (v: string, field: string) => {
      if (v === "" || v === null || v === undefined) {
        newErrors[field] = "This field is required.";
        return;
      }
      const n = Number(v);
      if (Number.isNaN(n)) newErrors[field] = "Enter a valid number.";
      else if (n < 0) newErrors[field] = "Value cannot be negative.";
    };

    if (!String(esicEmpId).trim()) newErrors.esic_emp_id = "ESIC Employee ID is required.";
    if (!salaryMonth) newErrors.salary_month = "Salary month is required.";
    if (!selectedConfigId) newErrors.config_id = "ESIC Configuration is required.";

    numOrErr(grossWages, "gross_wages");
    numOrErr(employeeContribution, "employee_contribution");
    numOrErr(employerContribution, "employer_contribution");

    if (!paymentStatus) newErrors.payment_status = "Payment status is required.";
    if (paymentStatus === "Paid") {
      if (!paymentDate) newErrors.payment_date = "Payment date is required for Paid status.";
      if (!challanNumber.trim()) newErrors.challan_number = "Challan number is required for Paid status.";
    }
    if (grossCap > 0 && Number(grossWages || 0) > grossCap) {
      newErrors.gross_wages = `For ESIC, Gross Wages must be ≤ ₹${grossCap.toLocaleString("en-IN")}.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (grossCap > 0 && Number(grossWages || 0) > grossCap) {
      message.warning(`ESIC is applicable only when Gross Wages ≤ ₹${grossCap.toLocaleString("en-IN")}.`);
      return;
    }
    if (!validateForm()) return;

    const payload = {
      esic_emp_id: Number(esicEmpId),
      salary_month: toMonthPayload(salaryMonth),
      gross_wages: Number(grossWages),
      esic_wages: Number(esicWages || grossWages || 0), // keep esic_wages, fallback to gross if empty
      employee_contribution: Number(employeeContribution),
      employer_contribution: Number(employerContribution),
      payment_status: paymentStatus,
      payment_date: paymentDate ? toDMY(paymentDate) : null,
      challan_number: challanNumber?.trim() || null,
      selectedConfigId: selectedConfigId != null ? Number(selectedConfigId) : null,
      Selected_Earning_IDs: selectedEarningIds || [],
    };

    if (modalType === "create") {
      const result: any = await dispatch(createEsicMonthlyContribution(payload));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("ESIC contribution created successfully");
        await dispatch(fetchEsicMonthlyContributionList());
        setTableKey((k) => k + 1);
        resetFields();
        handleCancel();
      } else {
        message.warning(result.payload?.error || "Failed to update");
      }
    } else if (modalType === "edit" && contributionId) {
      const result: any = await dispatch(updateEsicMonthlyContribution({ id: contributionId, payload }));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("ESIC contribution updated successfully");
        await dispatch(fetchEsicMonthlyContributionList());
        setTableKey((k) => k + 1);
        resetFields();
        handleCancel();
      } else {
        message.warning(result.payload?.error || "Failed to update");
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedRowData?.contribution_id) return;
    try {
      const result: any = await dispatch(deleteEsicMonthlyContribution(selectedRowData.contribution_id));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("ESIC contribution deleted successfully");
        setIsDeleteModalVisible(false);
        await dispatch(fetchEsicMonthlyContributionList());
        setTableKey((k) => k + 1);
      } else {
        message.warning(result.payload || "Failed to delete");
      }
    } catch {
      message.error("Something went wrong");
    }
  };

  // data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = list ?? [];

  const exportRows = useMemo(
    () =>
      data.map((item: any, idx: number) => {
        const sm: any = parseAnyDate(item.salary_month);
        const pd: any = parseAnyDate(item.payment_date);
        return {
          "S.No": idx + 1,
          "Employee Name": item.employee_name ?? "N/A",
          "Salary Month": sm?.isValid() ? sm.format("MM-YYYY") : "N/A",
          "Gross Wages": Number(item.gross_wages ?? 0),
          "ESIC Wages": Number(item.esic_wages ?? 0),
          "Employee Contribution": Number(item.employee_contribution ?? 0),
          "Employer Contribution": Number(item.employer_contribution ?? 0),
          "Payment Status": item.payment_status ?? "N/A",
          "Payment Date": pd?.isValid() ? pd.format("DD-MM-YYYY") : "N/A",
          "Challan No.": item.challan_number ?? "N/A",
        };
      }),
    [data]
  );

  const filteredData = useMemo(() => {
    let base = Array.isArray(data) ? [...data] : [];
    if (filterMonth) {
      base = base.filter((r) => monthKey(r.salary_month) === filterMonth);
    }

    if (selectedStatus && selectedStatus !== "All") {
      base = base.filter((r) => String(r.payment_status).toLowerCase() === selectedStatus.toLowerCase());
    }

    if (selectedSort === "Ascending") {
      base.sort((a, b) => (a.contribution_id ?? 0) - (b.contribution_id ?? 0));
    } else if (selectedSort === "Descending") {
      base.sort((a, b) => (b.contribution_id ?? 0) - (a.contribution_id ?? 0));
    } else if (selectedSort === "Recently Added") {
      base.sort((a, b) => (b.contribution_id ?? 0) - (a.contribution_id ?? 0));
    }

    return base;
  }, [data, selectedStatus, selectedSort, filterMonth]);

  useEffect(() => {
    setTableKey((k) => k + 1);
  }, [selectedStatus, selectedSort, list, filterMonth]);

  const config = useAppConfig();
  if (!config) return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const columns = useMemo(
    () => [
      {
        title: "Employee Name",
        dataIndex: "employee_name",
        render: (text: string, record: any) => (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img src={record.employee_picture_url ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                alt="img"
                className="img-fluid rounded-circle"
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                {record.employee_name}
              </p>
            </div>
          </div>
        ),
        sorter: (a: any, b: any) => a.Name.length - b.Name.length,
      },
      {
        title: "Salary Month",
        dataIndex: "salary_month",
        sorter: (a: any, b: any) => sortableEpoch(a.salary_month) - sortableEpoch(b.salary_month),
        render: (text: string) => <span>{formatSalaryMonth(text)}</span>,
      },
      {
        title: "Gross Wages",
        dataIndex: "gross_wages",
        sorter: (a: any, b: any) => Number(a.gross_wages) - Number(b.gross_wages),
        render: (n: number) => <span>{formatCurrency(n, config)}</span>,
      },
      {
        title: "ESIC Wages",
        dataIndex: "esic_wages",
        sorter: (a: any, b: any) => Number(a.esic_wages) - Number(b.esic_wages),
        render: (n: number) => <span>{formatCurrency(n, config)}</span>,
      },
      {
        title: "Employee Contr.",
        dataIndex: "employee_contribution",
        sorter: (a: any, b: any) => Number(a.employee_contribution) - Number(b.employee_contribution),
        render: (n: number) => <span>{formatCurrency(n, config)}</span>,
      },
      {
        title: "Employer Contr.",
        dataIndex: "employer_contribution",
        sorter: (a: any, b: any) => Number(a.employer_contribution) - Number(b.employer_contribution),
        render: (n: number) => <span>{formatCurrency(n, config)}</span>,
      },
      {
        title: "Payment",
        dataIndex: "payment_status",
        render: (text: string) => (
          <span
            className={`badge ${text === "Paid" ? "badge-success" : text === "Pending" ? "badge-warning" : "badge-danger"
              } d-inline-flex align-items-center badge-xs`}
          >
            <i className="ti ti-point-filled me-1" />
            {text}
          </span>
        ),
        sorter: (a: any, b: any) => sortableEpoch(a.payment_date) - sortableEpoch(b.payment_date),
      },
      {
        title: "Payment Date",
        dataIndex: "payment_date",
        render: (text: string | null) => <span>{formatPaymentDate(text)}</span>,
        sorter: (a: any, b: any) => {
          const da = dayjs(a.payment_date, "DD-MM-YYYY").valueOf() || 0;
          const db = dayjs(b.payment_date, "DD-MM-YYYY").valueOf() || 0;
          return da - db;
        },
      },
      {
        title: "Challan No.",
        dataIndex: "challan_number",
        render: (text: string | null) => <span>{text || "—"}</span>,
        sorter: (a: any, b: any) => (a.challan_number || "").localeCompare(b.challan_number || ""),
      },
      {
        title: "Action",
        dataIndex: "actions",
        render: (_: any, record: any) => (
          <div className="action-icon d-inline-flex">
            <button
              onClick={() => {
                if (canEdit) {
                  showModal("edit");
                  handleEdit(record);
                }
              }}
              className="me-2"
              disabled={!canEdit}
              style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={canEdit ? "Edit" : "No permission to edit"}
            >
              <i className="ti ti-edit" />
            </button>

            <button
              onClick={() => {
                if (canDelete) {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }
              }}
              className="me-2"
              disabled={!canDelete}
              style={{ cursor: !canDelete ? "not-allowed" : "pointer" }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={!canDelete ? "No permission to delete" : "Delete"}
            >
              <i className="ti ti-trash" />
            </button>
          </div>
        ),
      },
    ],
    [canEdit, canDelete]
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">ESIC Monthly Contribution</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="#">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    ESIC Monthly Contribution
                  </li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canExport && (
                <div className="me-2">
                  <div className="dropdown">
                    <Link to="#" className="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                      <i className="ti ti-file-export me-1" />
                      Export
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                      <li>
                        <button onClick={() => exportToCSV(exportRows, "esic_contributions")} className="dropdown-item rounded-1">
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as CSV
                        </button>
                      </li>
                      <li>
                        <button onClick={() => exportToExcel(exportRows, "esic_contributions")} className="dropdown-item rounded-1">
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel
                        </button>
                      </li>
                      <li>
                        <button onClick={() => exportToPDF(exportRows, "esic_contributions")} className="dropdown-item rounded-1">
                          <i className="ti ti-file-type-xls me-1" />
                          Export as PDF
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {canAdd && (
                <Button
                  type="primary"
                  onClick={() => showModal("create")}
                  className="d-inline-flex align-items-center  me-2"
                  style={{ paddingTop: "20px", paddingBottom: "20px" }}
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Contribution
                </Button>
              )}

              {canAdd && (
                <Button
                  type="default"
                  onClick={() => {
                    setBulkOpen(true);
                  }}
                  className="d-inline-flex align-items-center"
                  style={{ paddingTop: "20px", paddingBottom: "20px" }}
                >
                  <i className="ti ti-robot me-1" />
                  Auto ESIC Process
                </Button>
              )}
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Contribution List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                    {selectedStatus ? selectedStatus : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link to="#" onClick={() => setSelectedStatus("All")} className="dropdown-item rounded-1">
                        All
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedStatus("Paid")} className="dropdown-item rounded-1">
                        Paid
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedStatus("Pending")} className="dropdown-item rounded-1">
                        Pending
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="dropdown">
                  <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                    {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Recently Added"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="d-flex align-items-center ms-0  ms-md-3">
                  <input
                    type="month"
                    className="form-control form-control-sm"
                    style={{ minWidth: 150 }}
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                  />
                  {filterMonth && (
                    <button
                      type="button"
                      className="btn btn-sm bg-success ms-2 p-1"
                      onClick={() => setFilterMonth("")}
                      title="Clear month filter"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              <Table key={tableKey} dataSource={canView ? filteredData : []} columns={columns} Selection={true} />
            </div>
          </div>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      {/* Drawer: Create/Edit */}
      <Drawer
        title={modalType === "edit" ? "Edit Contribution" : "Add Contribution"}
        placement="right"
        width={700}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    ESIC Employee <span className="text-danger">*</span>
                  </label>
                  <select className="form-select" value={esicEmpId} onChange={(e) => setEsicEmpId(e.target.value)}>
                    <option value="">-- Select Employee --</option>
                    {esicEmployees.map((emp: any) => (
                      <option key={emp.esic_emp_id} value={emp.esic_emp_id}>
                        {emp.Name} ({emp.esic_number})
                      </option>
                    ))}
                  </select>
                  {errors.esic_emp_id && <div style={{ color: "red" }}>{errors.esic_emp_id}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Status <span className="text-danger">*</span>
                  </label>
                  <select className="form-select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                  {errors.payment_status && <div style={{ color: "red" }}>{errors.payment_status}</div>}
                </div>
              </div>
            </div>

            <div className="row col-md-12">
              <div className="mb-3">
                <label className="form-label">
                  Configuration <span className="text-danger">*</span>
                </label>
                <Select
                  className="w-100"
                  placeholder="Select ESIC Configuration"
                  value={selectedConfigId}
                  onChange={(id: number) => {
                    setSelectedConfigId(id);
                    const cfg = configList.find((c: any) => Number(c.config_id) === Number(id));
                    if (cfg) {
                      setEmpPct(Number(cfg.employee_contribution_rate) || 0);
                      setErPct(Number(cfg.employer_contribution_rate) || 0);
                      setGrossCap(Number(cfg.wage_limit) > 0 ? Number(cfg.wage_limit) : 0);
                    } else {
                      setEmpPct(0);
                      setErPct(0);
                      setGrossCap(0);
                    }
                  }}
                  options={configList.map((c: any) => ({
                    value: Number(c.config_id),
                    label: `${c.office_name} (${c.esic_code})`,
                  }))}
                />
                {errors.config_id && <div style={{ color: "red" }}>{errors.config_id}</div>}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Salary Month <span className="text-danger">*</span>
                  </label>
                  <input
                    type="month"
                    className="form-control"
                    disabled={modalType === "edit"}
                    value={salaryMonth}
                    onChange={(e) => setSalaryMonth(e.target.value)}
                  />
                  <small className="text-muted">Will be saved as MM-YYYY</small>
                  {errors.salary_month && <div style={{ color: "red" }}>{errors.salary_month}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Date {paymentStatus === "Paid" && <span className="text-danger">*</span>}
                  </label>
                  <input type="date" className="form-control" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                  <small className="text-muted">Will be saved as DD-MM-YYYY</small>
                  {errors.payment_date && <div style={{ color: "red" }}>{errors.payment_date}</div>}
                </div>
              </div>
            </div>

            <div className="row">
              {employeeData && (
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Gross Wages <span className="text-danger">*</span>
                  </label>

                  <Select
                    mode="multiple"
                    allowClear
                    className="w-100 custom-select"
                    placeholder="Select earnings to include in Gross Wages"
                    value={selectedEarningIds}
                    onChange={(ids: number[]) => {
                      setSelectedEarningIds(ids);
                      const sum = ids.reduce((acc, id) => {
                        const row = employeeData.earnings.find((e: any) => Number(e.id) === Number(id));
                        return acc + Number(row?.amount ?? 0);
                      }, 0);
                      setGrossWages(String(sum));
                    }}
                    options={(employeeData.earnings || []).map((earning: any) => ({
                      label: `${earning.earning_name} - ₹${earning.amount}`,
                      value: Number(earning.id),
                    }))}
                  />
                  <div className="mt-2">
                    <input
                      type="text"
                      className="form-control"
                      value={grossWages ? Number(grossWages).toLocaleString("en-IN") : ""}
                      readOnly
                      placeholder="Gross will auto-calculate from selected earnings"
                    />
                  </div>

                  {errors.gross_wages && <div style={{ color: "red" }}>{errors.gross_wages}</div>}
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Employee %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={empPct}
                    onChange={(e) => setEmpPct(Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Employer %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={erPct}
                    onChange={(e) => setErPct(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Employee Contribution <span className="text-danger">*</span>
                  </label>
                  <input type="number" step="0.01" className="form-control" value={employeeContribution} readOnly />
                  {errors.employee_contribution && <div style={{ color: "red" }}>{errors.employee_contribution}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Employer Contribution <span className="text-danger">*</span>
                  </label>
                  <input type="number" step="0.01" className="form-control" value={employerContribution} readOnly />
                  {errors.employer_contribution && <div style={{ color: "red" }}>{errors.employer_contribution}</div>}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Challan Number {paymentStatus === "Paid" && <span className="text-danger">*</span>}
                  </label>
                  <input type="text" className="form-control" value={challanNumber} onChange={(e) => setChallanNumber(e.target.value)} />
                  {errors.challan_number && <div style={{ color: "red" }}>{errors.challan_number}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light me-2" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || (grossCap > 0 && Number(grossWages || 0) > grossCap)}
              className="btn btn-primary"
            >
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              {modalType === "edit" ? "Update Contribution" : "Add Contribution"}
            </button>
          </div>
        </form>
      </Drawer>

      <Drawer
        title="Auto ESIC Process"
        placement="right"
        width={520}
        onClose={handleBulkClose}
        visible={bulkOpen}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <div className="row">
          <div className="col-12 ">
            <div className="mb-3">
              <label className="form-label">
                Configuration <span className="text-danger">*</span>
              </label>
              <Select
                className="w-100"
                placeholder="Select ESIC Configuration"
                value={bulkConfigId}
                onChange={(id: number) => {
                  setBulkConfigId(id);
                  const cfg = configList.find((c: any) => Number(c.config_id) === Number(id));
                  if (cfg) {
                    setBulkEmpPct(Number(cfg.employee_contribution_rate) || 0);
                    setBulkErPct(Number(cfg.employer_contribution_rate) || 0);
                    setGrossCap(Number(cfg.wage_limit) > 0 ? Number(cfg.wage_limit) : 0);
                  } else {
                    setBulkEmpPct(0);
                    setBulkErPct(0);
                    setGrossCap(0);
                  }
                }}
                options={configList.map((c: any) => ({
                  value: Number(c.config_id),
                  label: `${c.office_name} (${c.esic_code})`,
                }))}
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <label className="form-label">
              Salary Month <span className="text-danger">*</span>
            </label>
            <input
              type="month"
              className="form-control"
              value={bulkMonth}
              onChange={(e) => setBulkMonth(e.target.value)}
              placeholder="YYYY-MM"
            />
            <small className="text-muted">Will be saved as MM-YYYY</small>
          </div>

          <div className="col-12 mb-3">
            <label className="form-label">
              Payment Status <span className="text-danger">*</span>
            </label>
            <select className="form-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value as any)}>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
            </select>
          </div>

          <div className="col-6 mb-3">
            <label className="form-label">Employee %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="form-control"
              value={bulkEmpPct}
              onChange={(e) => setBulkEmpPct(Number(e.target.value))}
            />
          </div>

          <div className="col-6 mb-4">
            <label className="form-label">Employer %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="form-control"
              value={bulkErPct}
              onChange={(e) => setBulkErPct(Number(e.target.value))}
            />
          </div>

          <div className="col-12 mb-3">
            <label className="form-label">
              Earning Heads to include <span className="text-danger">*</span>
            </label>
            <Select
              mode="multiple"
              className="w-100 custom-select"
              value={bulkHeadIds}
              onChange={(vals) => setBulkHeadIds(vals as number[])}
              placeholder="Pick earning heads (e.g., Basic, HRA, TA)"
              options={(earnings || []).map((h: any) => ({
                value: Number(h.Id),
                label: h.Name,
              }))}
            />
            <small className="text-muted">
              For each employee, only salary-structure rows matching these heads will be summed into Gross.
            </small>
          </div>

          <div className="col-12">
            <small className="text-muted d-block mb-2">
              • Only ESIC-applicable employees with an active salary structure in the selected month will be processed.
              {grossCap > 0 && (
                <>
                  <br />• Records are created only when Gross Wages ≤ ₹{grossCap.toLocaleString("en-IN")}
                </>
              )}
            </small>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button className="btn btn-light me-2" onClick={handleBulkClose} disabled={bulkLoading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={bulkLoading || !bulkConfigId || !bulkMonth || isNaN(bulkEmpPct) || isNaN(bulkErPct)}
            onClick={async () => {
              if (!bulkConfigId) {
                message.error("Please select ESIC Configuration.");
                return;
              }
              if (!bulkMonth) {
                message.error("Please select Salary Month.");
                return;
              }
              if (bulkEmpPct < 0 || bulkErPct < 0) {
                message.error("Percentages must be 0 or higher.");
                return;
              }
              if (!bulkHeadIds.length) {
                message.error("Please choose at least one earning head.");
                return;
              }

              setBulkLoading(true);
              try {
                const ym = bulkMonth; // "YYYY-MM"
                const eligible = (esicEmployees || []).filter(
                  (e: any) => String(e.status).toLowerCase() === "active" && Number(e.is_esic_applicable) === 1
                );

                if (!eligible.length) {
                  message.warning("No ESIC-applicable employees found.");
                  setBulkLoading(false);
                  return;
                }

                let created = 0;
                let skippedGross = 0;
                let skippedNoStruct = 0;

                const results = await Promise.allSettled(
                  eligible.map(async (emp: any) => {
                    const matches = (salaryStructures || [])
                      .filter((ss: any) => ss.employee_id === emp.employee_id && overlapsMonth(ss.start_date, ss.end_date, ym))
                      .sort((a: any, b: any) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf());

                    const ss = matches[0];
                    if (!ss || !Array.isArray(ss.earnings) || !ss.earnings.length) {
                      skippedNoStruct++;
                      return;
                    }

                    const allowedIds = new Set(bulkHeadIds.map(Number));
                    const filteredRows = (ss.earnings || []).filter((r: any) => allowedIds.has(Number(r.earning_id)));
                    if (!filteredRows.length) {
                      skippedNoStruct++;
                      return;
                    }

                    const ids = filteredRows.map((r: any) => Number(r.id));
                    const gross = filteredRows.reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);

                    if (grossCap > 0 && Number(gross) > grossCap) {
                      skippedGross++;
                      return;
                    }

                    const empAmt = round2((gross * Number(bulkEmpPct || 0)) / 100);
                    const erAmt = round2((gross * Number(bulkErPct || 0)) / 100);

                    const payload = {
                      esic_emp_id: Number(emp.esic_emp_id),
                      salary_month: toMonthPayload(ym),
                      gross_wages: Number(gross),
                      esic_wages: Number(gross),
                      employee_contribution: empAmt,
                      employer_contribution: erAmt,
                      payment_status: bulkStatus,
                      payment_date: bulkStatus === "Paid" ? todayDMY() : null,
                      challan_number: null,
                      Selected_Earning_IDs: ids,
                      selectedConfigId: bulkConfigId,
                    };

                    await dispatch(createEsicMonthlyContribution(payload)).unwrap();
                    created++;
                  })
                );

                let failedAlreadyExists = 0;
                let failedOther = 0;

                for (const r of results) {
                  if (r.status === "rejected") {
                    const reason: any = (r as PromiseRejectedResult).reason;
                    const raw = reason?.response?.data ?? reason?.data ?? reason;
                    const msg =
                      typeof raw === "string"
                        ? raw
                        : raw?.error || raw?.message || raw?.statusText || JSON.stringify(raw);

                    if (/already exists/i.test(String(msg))) {
                      failedAlreadyExists++;
                    } else {
                      failedOther++;
                    }
                  }
                }

                if (created) message.success(`ESIC processed: ${created} created`);
                if (skippedGross) message.warning(`${skippedGross} skipped (Gross > ₹${grossCap.toLocaleString("en-IN")})`);
                if (skippedNoStruct) message.warning(`${skippedNoStruct} skipped (No salary structure/earnings for the month)`);

                if (failedAlreadyExists) {
                  message.warning(`${failedAlreadyExists} failed (already exists for the selected month)`);
                }
                if (failedOther) {
                  message.error(`${failedOther} failed (other errors)`);
                }

                await dispatch(fetchEsicMonthlyContributionList());
                setBulkOpen(false);
                resetBulkFields();
              } catch {
                message.error("Bulk ESIC processing failed.");
              } finally {
                setBulkLoading(false);
              }
            }}
          >
            {bulkLoading && <span className="spinner-border spinner-border-sm me-2" />}
            Process ESIC
          </button>
        </div>
      </Drawer>

      {/* Delete Confirm */}
      <DeleteModal1 isVisible={isDeleteModalVisible} onClose={() => setIsDeleteModalVisible(false)} onConfirm={handleDelete} />
    </>
  );
};

export default MonthlyContribution;
